import { OvFormInputReadOnly, OvInput, OvPasswordInput, OvSelect, OvAutoComplete } from '@components'
import { useContext, useEffect, useId, useState } from 'react'
import { Controller, FieldError, FieldValues, UseControllerProps, useFormContext } from 'react-hook-form'
import { OvFormContext } from '../ov-form-provider/ov-form-provider'
import { OvFormErrorContext } from '../ov-form/ov-form'
import { Checkbox, FormControlLabel, FormControlLabelProps, Switch } from '@mui/material'
import { IOvInput } from '../ov-input/ov-input'
import { IOvPasswordInput } from '../ov-password-input/ov-password-input'
import { IOvSelect } from '../ov-select/ov-select'
import { IOvPriceInput, OvPriceInput } from '../ov-price-input/ov-price-input'
import dayjs from 'dayjs'
import { DatePicker, DatePickerProps } from '@mui/x-date-pickers'
import { formatValueToCurrency } from '@helpers/formatCentToAmount.helper'
import { TextareaAutosize } from '@mui/base/TextareaAutosize'
import { IOvAutoCompleteProps } from '../ov-autocomplete/ov-autocomplete'
import { OvVATFormInput, IOvVATFormInput } from '../ov-form-vat-input/ov-form-vat-input'

type OmitId<T> = Omit<T, 'id'>

export type IOvFormInput<T extends FieldValues, I> = {
  id?: string
  label?: string
  name: keyof FieldValues
  readOnly?: boolean
  validate?: string
  loading?: boolean
  hideAsterisk?: boolean
  onError?: (arg: FieldError) => void
  withLabel?: boolean
  boldValue?: boolean
  highlighted?: boolean
  type?:
    | 'text'
    | 'textarea'
    | 'currency'
    | 'date'
    | 'checkbox'
    | 'switch'
    | 'select'
    | 'password'
    | 'vat'
    | IOvInput['type']
} & UseControllerProps<T> &
  OmitId<I>

type IOvSelectInputProps = Parameters<typeof OvSelect>[0]

type IOvAutoCompleteInputProps = Parameters<typeof OvAutoComplete>[0]

type IOvTextInputProps = Parameters<typeof OvInput>[0] & {
  type: 'text'
}

type IOvPasswordInputProps = Parameters<typeof OvPasswordInput>[0] & {
  type: 'password'
}

type IOvFileInputProps = Parameters<typeof OvInput>[0] & {
  type: 'file'
}

type IOvTextAreaInputProps = Parameters<typeof TextareaAutosize> & {
  type: 'textarea'
}

type IOvDateInputProps = DatePickerProps<any> & {
  type: 'date'
  readOnly?: boolean
}

type IOvCheckbox = Parameters<typeof FormControlLabel>[0] & {
  type: 'checkbox'
  label: string
  readonly?: boolean
} & Omit<FormControlLabelProps, 'onFocus' | 'label'>

type IOvSwitch = Parameters<typeof FormControlLabel>[0] & {
  type: 'switch'
  label: string
  readonly?: boolean
} & Omit<FormControlLabelProps, 'onFocus' | 'label'>

function OvFormInput<T extends FieldValues>(
  props: IOvFormInput<T, IOvSelectInputProps> & {
    type: 'select'
    readonly?: boolean
  }
): JSX.Element

function OvFormInput<T extends FieldValues>(
  props: IOvFormInput<T, IOvCheckbox> & {
    type: 'checkbox'
    readOnly?: boolean
  }
): JSX.Element

function OvFormInput<T extends FieldValues>(
  props: IOvFormInput<T, IOvSwitch> & {
    type: 'switch'
    readOnly?: boolean
  }
): JSX.Element

function OvFormInput<T extends FieldValues>(
  props: IOvFormInput<T, Omit<IOvInput, 'variant'>> & {
    readonly?: boolean
  }
): JSX.Element

function OvFormInput<T extends FieldValues>(
  props: IOvFormInput<T, IOvPriceInput> & {
    type: 'currency'
    readonly?: boolean
  }
): JSX.Element

function OvFormInput<T extends FieldValues>(
  props: IOvFormInput<T, IOvDateInputProps> & {
    type: 'date'
    readonly?: boolean
  }
): JSX.Element

function OvFormInput<T extends FieldValues>(
  props: IOvFormInput<T, IOvTextAreaInputProps> & {
    type: 'textarea'
  }
): JSX.Element

function OvFormInput<T extends FieldValues>(
  props: IOvFormInput<T, IOvPasswordInputProps> & {
    type: 'password'
    readOnly?: boolean
  }
): JSX.Element

function OvFormInput<T extends FieldValues>(
  props: IOvFormInput<T, Omit<IOvAutoCompleteInputProps, 'renderInput'>> & {
    type: 'autocomplete'
  }
): JSX.Element

function OvFormInput<T extends FieldValues>(
  props: IOvFormInput<T, IOvVATFormInput> & {
    type: 'vat'
    readonly?: boolean
  }
): JSX.Element

function OvFormInput<T extends FieldValues>({
  rules,
  name,
  id: customId,
  label = '',
  readOnly: customReadOnly,
  loading: customLoading,
  onError: customOnError,
  boldValue,
  highlighted,
  type = 'text',
  ...props
}: IOvFormInput<
  T,
  | IOvInput
  | IOvSelectInputProps
  | IOvCheckbox
  | IOvSwitch
  | IOvPriceInput
  | IOvDateInputProps
  | IOvTextAreaInputProps
  | IOvTextInputProps
  | IOvFileInputProps
  | IOvAutoCompleteInputProps
  | IOvVATFormInput
>) {
  const [error, setError] = useState<FieldError | undefined>()
  const { control, register } = useFormContext<T>()
  const formContext = useContext(OvFormContext)
  const formErrorContext = useContext(OvFormErrorContext)
  const id = customId || name + useId()

  const readOnly = customReadOnly || formContext?.readOnly
  const loading = customLoading || formContext?.loading
  const onError = customOnError || formErrorContext?.onError
  // const register = formContext?.registerInput

  // Styling for highlighted fields (e.g., KVK prefilled fields)
  const highlightedStyle = {
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: highlighted ? '#2e7d32' : 'rgba(0, 0, 0, 0.23)',
        borderWidth: highlighted ? '2px' : '1px',
        transition: 'border-color 0.3s ease-in-out, border-width 0.3s ease-in-out',
      },
      '&:hover fieldset': {
        borderColor: highlighted ? '#2e7d32' : 'rgba(0, 0, 0, 0.87)',
      },
      '&.Mui-focused fieldset': {
        borderColor: highlighted ? '#2e7d32' : '#1976d2',
      },
    },
    '& .MuiInputLabel-root': {
      color: highlighted ? '#2e7d32' : 'rgba(0, 0, 0, 0.6)',
      fontWeight: highlighted ? 500 : 400,
      transition: 'color 0.3s ease-in-out, font-weight 0.3s ease-in-out',
    },
  }

  useEffect(() => {
    register(name, { ...rules })
  }, [])

  useEffect(() => {
    error && onError?.(error)
  }, [error])

  return (
    <div>
      <Controller
        name={name}
        control={control}
        render={context => {
          switch (type) {
            case 'select': {
              const _props = props as IOvSelect
              const _readOnlyValue = _props.options.find(el =>
                _props.output === 'option'
                  ? el[_props.valueKey] === context.field.value[_props.valueKey]
                  : el[_props.valueKey] === context.field.value
              )

              return readOnly ? (
                <OvFormInputReadOnly
                  {...context.field}
                  {...props}
                  label={label}
                  value={
                    _readOnlyValue
                      ? _props.renderOption
                        ? _props.renderOption(_readOnlyValue)
                        : _readOnlyValue[_props.labelKey]
                      : ''
                  }
                />
              ) : (
                <OvSelect
                  id={id}
                  readOnly={readOnly}
                  sx={{ minWidth: 'initial' }}
                  disabled={readOnly || loading}
                  error={!!context.fieldState.error}
                  {...context.field}
                  {..._props}
                  label={label}
                  helperText={context.fieldState.error?.message || _props.helperText}
                />
              )
            }

            case 'vat': {
              const _props = props as IOvVATFormInput
              return (
                <OvVATFormInput
                  id={id}
                  readOnly={readOnly}
                  sx={{ minWidth: 'initial' }}
                  disabled={readOnly || loading}
                  error={!!context.fieldState.error}
                  {...context.field}
                  {..._props}
                  label={label}
                  helperText={context.fieldState.error?.message || _props.helperText}
                />
              )
            }

            case 'autocomplete': {
              const _props = props as IOvAutoCompleteProps
              return readOnly ? (
                <OvFormInputReadOnly
                  {...context.field}
                  {...props}
                  label={label}
                  value={context.field.value?.[_props?.optionLabelKey]}
                />
              ) : (
                <OvAutoComplete
                  {..._props}
                  {...context.field}
                  value={context.field.value}
                  onChange={(e, val) => context.field.onChange(val)}
                  label={label}
                  sx={{ ...(_props.sx || {}), ...highlightedStyle }}
                />
              )
            }

            case 'switch': {
              return readOnly ? (
                <OvFormInputReadOnly
                  {...context.field}
                  {...props}
                  label={label}
                  value={context.field.value ? 'Ja' : 'Nee'}
                />
              ) : (
                <FormControlLabel
                  {...(props as Partial<FormControlLabelProps>)}
                  label={label}
                  {...context.field}
                  control={<Switch checked={context.field.value} />}
                  labelPlacement="start"
                />
              )
            }

            case 'checkbox': {
              const customOnChange = (props as any).onChange
              const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
                // Call custom onChange if provided
                if (customOnChange) {
                  customOnChange(event)
                }
                // Call react-hook-form onChange if custom handler didn't prevent default
                if (!event.defaultPrevented) {
                  context.field.onChange(event.target.checked)
                }
              }

              return readOnly ? (
                <OvFormInputReadOnly
                  {...context.field}
                  {...props}
                  label={label}
                  value={context.field.value ? 'Ja' : 'Nee'}
                />
              ) : (
                <FormControlLabel
                  {...(props as Partial<FormControlLabelProps>)}
                  label={label}
                  control={
                    <Checkbox
                      {...context.field}
                      checked={!!context.field.value}
                      onChange={handleChange}
                      disabled={context.field.disabled || readOnly}
                    />
                  }
                />
              )
            }

            case 'currency': {
              const { ref, ...fieldWithoutRef } = context.field
              return readOnly ? (
                <OvFormInputReadOnly
                  {...fieldWithoutRef}
                  {...props}
                  label={label}
                  value={formatValueToCurrency(context.field.value)}
                />
              ) : (
                <OvPriceInput
                  id={id}
                  inputRef={ref}
                  sx={{ minWidth: 'initial' }}
                  readOnly={readOnly}
                  disabled={readOnly || loading}
                  error={!!context.fieldState.error}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  {...(props as any)}
                  {...fieldWithoutRef}
                  onChange={(value: number) => context.field.onChange(value * 100)}
                  value={context.field.value / 100}
                  label={label}
                  helperText={context.fieldState.error?.message || (props as any).helperText}
                />
              )
            }

            case 'date':
              return readOnly ? (
                <OvFormInputReadOnly
                  {...context.field}
                  {...props}
                  label={label}
                  value={dayjs(context.field.value).format('DD-MM-YYYY')}
                />
              ) : (
                <DatePicker
                  readOnly={readOnly}
                  disabled={readOnly || loading}
                  sx={{ width: '100%' }}
                  format="DD-MM-YYYY"
                  slotProps={{
                    textField: {
                      error: !!context.fieldState.error,
                      helperText: context.fieldState.error?.message || (props as any).helperText,
                    },
                  }}
                  {...props}
                  defaultValue={props.defaultValue ? dayjs(props.defaultValue) : undefined}
                  value={context.field.value ? dayjs(context.field.value) : null}
                  onChange={date => {
                    // Convert dayjs object to ISO string for API compatibility
                    context.field.onChange(date ? date.toISOString() : null)
                  }}
                  label={label}
                />
              )

            case 'textarea':
              return readOnly ? (
                <OvFormInputReadOnly
                  {...context.field}
                  {...props}
                  label={label}
                  value={context.field.value || ''}
                />
              ) : (
                <OvInput
                  readOnly={readOnly}
                  disabled={readOnly || loading}
                  variant="outlined"
                  multiline
                  // slotProps={{
                  //   textField: {
                  //     error: !!context.fieldState.error,
                  //     helperText: context.fieldState.error?.message,
                  //   },
                  // }}
                  {...props}
                  {...context.field}
                  label={label}
                  defaultValue={props.defaultValue}
                  value={context.field.value}
                />
              )

            case 'password': {
              const { ref, ...fieldWithoutRef } = context.field
              return readOnly ? (
                <OvFormInputReadOnly
                  {...fieldWithoutRef}
                  {...props}
                  label={label}
                  value="••••••••"
                />
              ) : (
                <OvPasswordInput
                  id={id}
                  inputRef={ref}
                  readOnly={readOnly}
                  disabled={readOnly || loading}
                  error={!!context.fieldState.error}
                  sx={{ minWidth: 'initial' }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  {...(props as IOvPasswordInput)}
                  {...fieldWithoutRef}
                  label={label}
                  helperText={context.fieldState.error?.message || (props as any).helperText}
                />
              )
            }

            default: {
              const { ref, ...fieldWithoutRef } = context.field
              // For description fields, don't show '-' for empty values
              const isDescriptionField = typeof name === 'string' && name.includes('description')
              const displayValue = isDescriptionField ? context.field.value : (context.field.value ?? '-')

              return readOnly ? (
                <OvFormInputReadOnly
                  {...fieldWithoutRef}
                  {...props}
                  label={label}
                  value={displayValue}
                  boldValue={boldValue}
                />
              ) : (
                <OvInput
                  type="text"
                  id={id}
                  inputRef={ref}
                  readOnly={readOnly}
                  disabled={readOnly || loading}
                  error={!!context.fieldState.error}
                  sx={{ minWidth: 'initial', ...highlightedStyle }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  {...(props as OmitId<IOvInput>)}
                  {...fieldWithoutRef}
                  value={context.field.value}
                  label={label}
                  helperText={context.fieldState.error?.message || (props as any).helperText}
                />
              )
            }
          }
        }}
      />
    </div>
  )
}

export { OvFormInput }
